import { Box, type BoxProps, Text } from '@chakra-ui/react';
import { type OptionBase, Select, type SingleValue } from 'chakra-react-select';
import classNames from 'classnames';
import type { OptionProps } from 'react-select';

import styles from './filterSelectTheme.module.css';

interface NotToggleOption extends OptionBase {
  label: string;
  displayLabel: string;
  value: boolean;
  showDefault?: boolean;
}

interface NotToggleProps {
  filterName: string;
  isNot: boolean;
  onChange: (isNot: boolean) => void;
}

const options: NotToggleOption[] = [
  {
    label: 'Is any of',
    displayLabel: 'is any',
    value: false,
    showDefault: true,
  },
  { label: 'Is not', displayLabel: 'is not', value: true },
];

const CustomOption = (props: OptionProps<NotToggleOption, false>) => {
  const { data, innerRef, innerProps, isFocused } = props;
  return (
    <Box
      ref={innerRef}
      className={classNames(
        styles.optionRow,
        isFocused && styles.optionRowFocused
      )}
      {...innerProps}
    >
      <Text as="span" className={styles.optionLabel}>
        {data.label}
      </Text>
      {data.showDefault && (
        <Text as="span" className={styles.optionDefault}>
          Default
        </Text>
      )}
    </Box>
  );
};

export const NotToggle: React.FC<
  NotToggleProps & Omit<BoxProps, 'children' | 'onChange'>
> = ({ filterName, isNot, onChange, ...boxProps }) => {
  const handleChange = (selected: SingleValue<NotToggleOption>) => {
    if (selected) {
      onChange(selected.value);
    }
  };

  const formatOptionLabel = ({ displayLabel }: NotToggleOption) => {
    return displayLabel;
  };

  return (
    <Box
      pl={1}
      py={1}
      display="flex"
      alignItems="center"
      gap={1}
      minHeight="28px"
      flexShrink={0}
      {...boxProps}
    >
      <Text className={styles.notLabel}>{filterName}</Text>
      <Text className={styles.notLabel}>·</Text>
      <Box
        width="47px"
        flexShrink={0}
        position="relative"
        zIndex={10}
        overflow="visible"
      >
        <Select<NotToggleOption>
          options={options}
          value={options.find((opt) => opt.value === isNot) || null}
          onChange={handleChange}
          formatOptionLabel={formatOptionLabel}
          size="sm"
          isSearchable={false}
          menuPortalTarget={document.body}
          components={{
            // Type assertion needed due to react-select version mismatch between chakra-react-select and react-select
            // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
            Option: CustomOption as any,
          }}
          styles={{
            menuPortal: (base) => ({
              ...base,
              zIndex: 9999,
            }),
            option: (base) => ({
              ...base,
              backgroundColor: 'transparent',
            }),
            control: (base) => ({
              ...base,
              minHeight: 20,
            }),
          }}
          chakraStyles={{
            container: (provided) => ({
              ...provided,
              width: '100%',
            }),
            control: (provided) => ({
              ...provided,
              minHeight: '20px',
              height: '20px',
              backgroundColor: 'transparent',
              border: 'none',
              boxShadow: 'none',
              cursor: 'pointer',
              padding: 0,
              gap: '2px',
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
              '&:focus-within': {
                border: 'none',
                boxShadow: 'none',
              },
            }),
            valueContainer: (provided) => ({
              ...provided,
              padding: 0,
              fontSize: '12px',
              color: 'var(--frs-text-muted, var(--chakra-colors-gray-600))',
              fontWeight: '500',
              gap: '1px',
              flexWrap: 'nowrap',
              flex: '0 1 auto',
            }),
            singleValue: (provided) => ({
              ...provided,
              color: 'var(--frs-text-muted, var(--chakra-colors-gray-500))',
              fontWeight: '600',
              fontSize: '12px',
              margin: 0,
            }),
            indicatorsContainer: (provided) => ({
              ...provided,
              padding: 0,
              height: '20px',
              width: '14px',
              marginLeft: 'auto',
              justifyContent: 'center',
            }),
            dropdownIndicator: (provided) => ({
              ...provided,
              padding: 0,
              color: 'var(--frs-text-muted, var(--chakra-colors-gray-600))',
              marginLeft: 0,
              svg: {
                width: '10px',
                height: '10px',
              },
            }),
            indicatorSeparator: () => ({
              display: 'none',
            }),
            menu: (provided) => ({
              ...provided,
              minWidth: '140px',
              boxShadow: 'md',
              marginTop: '0px',
              paddingY: 0,
              backgroundColor: 'var(--frs-menu-bg, #ffffff)',
              border: '1px solid var(--frs-border, transparent)',
            }),
            menuList: (provided) => ({
              ...provided,
              paddingTop: 0,
              paddingBottom: 0,
            }),
            option: (provided, state) => {
              const backgroundColor = state.isFocused
                ? 'var(--frs-option-hover-bg, var(--chakra-colors-gray-50))'
                : 'transparent';
              const textColor = state.isDisabled
                ? 'var(--frs-text-disabled, var(--chakra-colors-gray-400))'
                : 'var(--frs-option-text, var(--chakra-colors-navyBlue-500))';
              return {
                ...provided,
                fontSize: '12px',
                color: textColor,
                fontWeight: 500,
                padding: '6px 12px',
                backgroundColor,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                columnGap: '20px',
              };
            },
          }}
        />
      </Box>
    </Box>
  );
};
